import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

// Inline income event editor. Users click any scheduled income row on a
// future pay-period card and choose between:
//   (a) Just this occurrence — writes an override into the Income row's
//       `overrides` map (per-date amount replacement); doesn't change the
//       recurring baseline.
//   (b) Update the recurring source — patches the Income's `amount` field,
//       affecting THIS occurrence and every future one.
export default function EditIncomeEventModal({ event, onClose, onSaved }) {
  const [amount, setAmount] = useState(String(event.amount));
  const [scope, setScope] = useState("instance");
  const [saving, setSaving] = useState(false);
  const isRecurring = event.frequency && event.frequency !== "unscheduled";

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      if (!isRecurring || scope === "source") {
        await api.patch(`/incomes/${event.income_id}`, { amount: amt });
        toast.success(`Updated ${event.source} — applies to every future paycheck`);
      } else {
        // Instance-only: merge into the existing overrides map.
        const { data: inc } = await api.get(`/incomes`);
        const target = (inc || []).find((x) => x.id === event.income_id);
        const nextOverrides = { ...(target?.overrides || {}), [event.date]: amt };
        await api.patch(`/incomes/${event.income_id}`, { overrides: nextOverrides });
        toast.success(`Overrode ${event.source} amount for ${event.date}`);
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  // Delete the whole recurring series (or the one-time income row). We keep
  // this available directly from the Dashboard because legacy recurring rows
  // with zero balance / non-standard status still project into future pay
  // periods but might not surface on the Income page.
  const deleteSeries = async () => {
    const label = isRecurring ? "recurring series" : "income";
    if (!window.confirm(`Delete ${label} "${event.source}"?\n\nThis removes every projected occurrence from every future pay period.`)) return;
    setSaving(true);
    try {
      await api.delete(`/incomes/${event.income_id}`);
      toast.success(`Deleted ${event.source}`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#2C302E]/50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        className="card-soft p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} onSubmit={save}
        data-testid="edit-income-event-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#4A6148]">Edit income</div>
            <h3 className="font-heading text-xl text-[#2C302E] mt-1">{event.source}</h3>
            <p className="text-xs text-[#59605C] mt-1">Occurrence on {event.date}</p>
          </div>
          <button type="button" onClick={onClose} className="text-[#8A918D] hover:text-[#2C302E]">
            <X size={18} />
          </button>
        </div>
        <label className="block">
          <span className="text-xs font-semibold uppercase text-[#59605C]">Amount</span>
          <input
            type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="input-soft mt-1" required autoFocus
            data-testid="edit-income-event-amount"
          />
        </label>
        {isRecurring && (
          <fieldset className="mt-3 rounded-xl border border-[#E5E0D5] p-3 bg-[#FBFAF6]"
            data-testid="edit-income-event-scope">
            <legend className="px-1 text-[10px] font-semibold uppercase tracking-widest text-[#59605C]">Apply this change to</legend>
            <label className="flex items-start gap-2 py-1.5 cursor-pointer">
              <input type="radio" name="income-scope" value="instance" checked={scope === "instance"}
                onChange={() => setScope("instance")} className="mt-1 accent-[#4A6148]"
                data-testid="edit-income-event-scope-instance" />
              <span className="text-xs text-[#2C302E] leading-snug">
                <span className="font-semibold">Just this occurrence</span>
                <span className="block text-[#59605C]">Only the {event.date} paycheck changes. Future ones keep the current amount.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 py-1.5 cursor-pointer">
              <input type="radio" name="income-scope" value="source" checked={scope === "source"}
                onChange={() => setScope("source")} className="mt-1 accent-[#4A6148]"
                data-testid="edit-income-event-scope-source" />
              <span className="text-xs text-[#2C302E] leading-snug">
                <span className="font-semibold">The whole recurring source</span>
                <span className="block text-[#59605C]">Every past and future paycheck from this source changes.</span>
              </span>
            </label>
          </fieldset>
        )}
        <div className="mt-6 flex justify-between items-center gap-2 flex-wrap">
          <button
            type="button"
            className="text-xs text-[#8C3A30] hover:underline underline-offset-4 decoration-dotted disabled:opacity-50"
            onClick={deleteSeries}
            disabled={saving}
            data-testid="edit-income-event-delete-series"
          >
            {isRecurring ? "Delete recurring series" : "Delete income"}
          </button>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary"
              data-testid="edit-income-event-save">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
